import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, money } from "@/lib/api";

const COLS = {
  driver: [["name", "Driver"], ["customer_rating", "Rating"], ["acceptance_rate", "Acceptance %"], ["completion_rate", "Completion %"], ["on_time_rate", "On time %"],
    ["quality_rate", "Quality %"], ["drives_this_month", "Drives (month)"], ["funds_this_month", "Funds (month)"], ["complaints_count", "Complaints"],
    ["speeding_this_month", "Speeding (month)"], ["speeding_all_time", "Speeding (all)"], ["arrival_cancels_this_month", "Cancels after arrival (month)"]],
  customer: [["name", "Customer"], ["average_rating", "Rating"], ["trips_this_month", "Trips (month)"], ["completion_rate", "Completion %"], ["complaints_count", "Complaints"], ["arrival_cancels_this_month", "Cancels after arrival (month)"], ["on_hold", "On hold"]],
};

const cell = (k, v) => (v == null ? "—" : k === "funds_this_month" ? money(v) : k === "on_hold" ? (v ? "On hold" : "—") : typeof v === "number" && k.endsWith("rating") ? v.toFixed(2) : String(v));
const dur = (s) => (s == null ? "—" : s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`);
const road = (e) => e.road_name || (e.road_type ? `${e.road_type} road` : "Unknown road");

export const SpeedingBadge = ({ testId }) => (
  <span data-testid={testId} className="ml-2 inline-flex whitespace-nowrap rounded-full border border-red-500/50 bg-red-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-300">Speeding record</span>
);

export const SpeedingEventsTable = ({ events, testId }) => (
  <>
  <div className="space-y-2 sm:hidden" data-testid={`${testId}-list`}>
    {events.map((e) => (
      <div key={e.id} className="rounded-lg border border-white/5 px-3 py-2 text-xs text-zinc-300">
        <div className="flex justify-between gap-2"><span>{fmtDate(e.at)}</span><span className="font-semibold text-red-300">+{e.mph_over ?? "—"} mph</span></div>
        <p className="mt-0.5 text-zinc-400">{road(e)} · {Math.round(e.speed_mph)} in {e.limit_mph} mph{e.limit_source === "default" ? " (default)" : ""} · {dur(e.duration_s)}</p>
      </div>))}
  </div>
  <table className="hidden w-full min-w-[560px] text-xs sm:table" data-testid={testId}>
    <thead><tr className="text-left text-zinc-500">{["Date / time", "Road", "Posted limit", "Speed", "Over", "Duration"].map((h) => <th key={h} className="px-2 py-1.5 font-medium">{h}</th>)}</tr></thead>
    <tbody>{events.map((e) => (
      <tr key={e.id} data-testid={`speeding-event-${e.id}`} className="border-t border-white/5 text-zinc-300">
        <td className="whitespace-nowrap px-2 py-1.5">{fmtDate(e.at)}</td>
        <td className="px-2 py-1.5">{road(e)}</td>
        <td className="px-2 py-1.5">{e.limit_mph} mph{e.limit_source === "default" ? " (default)" : ""}</td>
        <td className="px-2 py-1.5">{Math.round(e.speed_mph)} mph</td>
        <td className="px-2 py-1.5 text-red-300">+{e.mph_over ?? "—"} mph</td>
        <td className="px-2 py-1.5">{dur(e.duration_s)}</td>
      </tr>))}</tbody>
  </table>
  </>
);

const DriverSpeeding = ({ id }) => {
  const [d, setD] = useState(null);
  const [skip, setSkip] = useState(0);
  useEffect(() => { api.get(`/admin/drivers/${id}/speeding-events`, { params: { limit: 25, skip } }).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))); }, [id, skip]);
  if (!d) return <p className="px-2 py-3 text-xs text-zinc-500">Loading…</p>;
  if (!d.total) return <p data-testid={`admin-speeding-none-${id}`} className="px-2 py-3 text-xs text-zinc-500">No speeding violations.</p>;
  return (
    <div className="space-y-2 px-2 py-3" data-testid={`admin-speeding-detail-${id}`}>
      <div className="overflow-x-auto"><SpeedingEventsTable events={d.events} testId={`admin-speeding-events-${id}`} /></div>
      {d.total > 25 && <div className="flex items-center gap-3 text-xs text-zinc-400">
        <button data-testid={`admin-speeding-prev-${id}`} disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - 25))} className="text-amber-300 disabled:opacity-40">Newer</button>
        <span>{skip + 1}–{Math.min(skip + 25, d.total)} of {d.total}</span>
        <button data-testid={`admin-speeding-next-${id}`} disabled={skip + 25 >= d.total} onClick={() => setSkip(skip + 25)} className="text-amber-300 disabled:opacity-40">Older</button>
      </div>}
    </div>
  );
};

export const RatingsAdmin = () => {
  const [role, setRole] = useState("driver");
  const [rows, setRows] = useState([]);
  const [sort, setSort] = useState(["name", 1]);
  const [comments, setComments] = useState([]);
  const [onlySpeeding, setOnlySpeeding] = useState(false);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/admin/ratings", { params: role === "driver" && onlySpeeding ? { role, has_speeding: true } : { role } }).then(({ data }) => setRows(data)); }, [role, onlySpeeding]);
  useEffect(() => { api.get("/admin/comments").then(({ data }) => setComments(data)); }, []);
  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const [k, d] = sort; const x = a[k] ?? -1; const y = b[k] ?? -1;
    return (typeof x === "string" ? x.localeCompare(y) : x - y) * d;
  }), [rows, sort]);
  const cols = COLS[role];
  return (
    <div className="space-y-6">
      <div className="lux-card p-3 sm:p-5" data-testid="admin-ratings">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {["driver", "customer"].map((r) => <button key={r} data-testid={`admin-ratings-role-${r}`} onClick={() => { setRole(r); setSort(["name", 1]); setOpen(null); }} className={`rounded-full px-4 py-1.5 text-sm capitalize ${role === r ? "bg-amber-400 text-black" : "border border-white/10 text-zinc-300"}`}>{r}s</button>)}
          {role === "driver" && <label className="ml-auto flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" data-testid="admin-ratings-has-speeding" checked={onlySpeeding} onChange={(e) => setOnlySpeeding(e.target.checked)} /> Has speeding violations</label>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm" data-testid="admin-ratings-table">
            <thead><tr className="text-left">{cols.map(([k, l]) => (
              <th key={k} className="px-2 py-2"><button data-testid={`admin-ratings-sort-${k}`} onClick={() => setSort(([sk, d]) => [k, sk === k ? -d : k === "name" ? 1 : -1])} className="lux-eyebrow inline-flex items-center gap-1 !text-[0.6rem]">{l}<ArrowUpDown className="h-3 w-3" /></button></th>
            ))}</tr></thead>
            <tbody>{sorted.map((r) => (
              <Fragment key={r.id}>
                <tr data-testid={`admin-ratings-row-${r.id}`} onClick={() => role === "driver" && setOpen(open === r.id ? null : r.id)} className={`border-t border-white/5 ${role === "driver" ? "cursor-pointer hover:bg-white/[0.02]" : ""}`}>{cols.map(([k]) => (
                  <td key={k} className={`px-2 py-2.5 ${(k === "on_hold" && r.on_hold) || ((k.startsWith("speeding") || k === "arrival_cancels_this_month") && r[k] > 0) ? "text-red-300" : "text-zinc-300"}`}>
                    {cell(k, r[k])}{k === "name" && r.speeding_record_badge && <SpeedingBadge testId={`admin-speeding-badge-${r.id}`} />}
                    {k === "name" && r.termination_pending && <span data-testid={`admin-termination-badge-${r.id}`} className="ml-2 inline-flex whitespace-nowrap rounded-full border border-red-500/50 bg-red-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-300">Termination pending</span>}
                  </td>))}</tr>
                {open === r.id && <tr className="bg-white/[0.02]"><td colSpan={cols.length}><DriverSpeeding id={r.id} /></td></tr>}
              </Fragment>
            ))}</tbody>
          </table>
        </div>
      </div>
      <div className="lux-card p-4 sm:p-6" data-testid="admin-comments">
        <p className="lux-eyebrow mb-3">All comments</p>
        {comments.map((c) => (
          <div key={c.id} className="border-t border-white/5 py-2.5 text-sm first:border-0">
            <p className="text-zinc-200">“{c.text}”</p>
            <p className="text-xs text-zinc-500">{c.author_name} ({c.author_role}) → {c.subject_name} ({c.subject_role}) · {fmtDate(c.trip_date)} · <Link to={`/trips/${c.trip_id}/feedback`} className="text-amber-300">trip</Link></p>
          </div>
        ))}
      </div>
    </div>
  );
};

const ComplaintRow = ({ c, onSaved }) => {
  const [note, setNote] = useState(c.admin_note || "");
  const save = async (status) => {
    try { await api.patch(`/admin/complaints/${c.id}`, { status, note }); toast.success(`Complaint ${status.toLowerCase()}`); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid={`admin-complaint-${c.id}`} className="space-y-2 border-t border-white/5 py-4 first:border-0">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className={`rounded-full px-2.5 py-0.5 text-xs ${c.status === "Open" ? "bg-amber-400/15 text-amber-200" : c.status === "Resolved" ? "bg-emerald-400/10 text-emerald-300" : "bg-white/5 text-zinc-400"}`} data-testid={`admin-complaint-status-${c.id}`}>{c.status}</span>
        <span className="text-amber-100">{c.category}</span>
        <span className="text-zinc-500">· {c.author_name} ({c.author_role}) about <span className="text-zinc-300">{c.subject_name}</span> ({c.subject_role}) · {fmtDate(c.created_at)}</span>
        <Link to={`/trips/${c.trip_id}/feedback`} className="text-xs text-amber-300">trip</Link>
      </div>
      <p className="text-sm text-zinc-300">{c.description}</p>
      <div className="flex flex-wrap gap-2">
        <input data-testid={`admin-complaint-note-${c.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Admin note" className="lux-input !w-auto min-w-0 flex-1 !py-2 text-sm" />
        <button data-testid={`admin-complaint-resolve-${c.id}`} onClick={() => save("Resolved")} className="btn-gold !py-2 text-sm">Resolve</button>
        <button data-testid={`admin-complaint-dismiss-${c.id}`} onClick={() => save("Dismissed")} className="btn-outline-gold !py-2 text-sm">Dismiss</button>
      </div>
    </div>
  );
};

export const ComplaintsAdmin = () => {
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [held, setHeld] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/complaints", { params: status ? { status } : {} }).then(({ data }) => setRows(data));
    api.get("/admin/ratings", { params: { role: "customer" } }).then(({ data }) => setHeld(data.filter((c) => c.on_hold)));
  }, [status]);
  useEffect(() => { load(); }, [load]);
  const reinstate = async (id) => { try { await api.post(`/admin/customers/${id}/reinstate`); toast.success("Customer reinstated"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-6">
      {held.length > 0 && (
        <div className="lux-card border-red-500/30 p-4 sm:p-6" data-testid="admin-held-customers">
          <p className="lux-eyebrow mb-3">Customers on hold</p>
          {held.map((c) => <div key={c.id} className="flex items-center justify-between gap-3 py-1.5 text-sm"><span className="text-red-200">{c.name} · {c.complaints_count} complaints</span>
            <button data-testid={`admin-reinstate-${c.id}`} onClick={() => reinstate(c.id)} className="btn-outline-gold !py-1.5 text-xs">Reinstate</button></div>)}
        </div>
      )}
      <div className="lux-card p-4 sm:p-6" data-testid="admin-complaints">
        <div className="mb-3 flex flex-wrap gap-2">
          {["", "Open", "Resolved", "Dismissed"].map((s) => <button key={s || "all"} data-testid={`admin-complaints-filter-${s || "all"}`} onClick={() => setStatus(s)} className={`rounded-full px-3 py-1 text-xs ${status === s ? "bg-amber-400 text-black" : "border border-white/10 text-zinc-300"}`}>{s || "All"}</button>)}
        </div>
        {rows.length === 0 && <p className="text-sm text-zinc-500">No complaints.</p>}
        {rows.map((c) => <ComplaintRow key={`${c.id}-${c.status}`} c={c} onSaved={load} />)}
      </div>
    </div>
  );
};
